/**
 * serbestKwAsimi.js — helpers of the organizer's "Stands Above the Free kW" page (backlog #500, 2026-10-19; backend #499).
 *
 * Backend: GET /portal/organizator/{fuarId}/talepler/serbest-kw-asimi ?search&salonId&degerlendirilemeyen&page(0-based)&size(<=100)
 *   -> { kural {mod FLAT|BLOCK|BANDS|NONE, serbestKw, blokKw, blokM2, kademeler[{minM2, maxM2, serbestKw}]},
 *        ozet {elektrikTalepSayisi, asanStandSayisi, toplamAsanKw, degerlendirilemeyenSayisi}, liste ASAN|DEGERLENDIRILEMEYEN,
 *        records [{standId, standName, salonId, salonName, katilimciFirmaName, dekoratorFirmaName, alanM2, serbestKw, istenenKw, asanKw, status, ...}],
 *        total, page, size, totalPages }.
 * The area is the KROKI area (#455), never the m² an exhibitor typed in a declaration. A row with `asanKw` null cannot be judged (no drawn area where the
 * rule needs one, or an area in no band).
 *
 * The page's state lives in the address (?ara=A12&salon=3&degerlendirilemeyen=1&sayfa=2), like the request filters (#458): bookmarkable, survives a reload.
 * Pure on purpose (no React, no i18n): the page, the header button and the tests use it.
 */

export const SAYFA_BOYUTU = 20;

/** The address keys this page owns (any other parameter of the address is left alone). */
const ANAHTARLAR = ['ara', 'salon', 'degerlendirilemeyen', 'sayfa'];

/** A positive integer from the address, else null. */
function pozitifTam(v) {
  const s = String(v ?? '').trim();
  return /^[0-9]+$/.test(s) && Number(s) > 0 ? Number(s) : null;
}

/** URLSearchParams (or anything with .get) -> { ara, salon, degerlendirilemeyen, sayfa }; `sayfa` is ZERO-based here (the address counts from 1). */
export function adresOku(params) {
  const al = (k) => (params && typeof params.get === 'function' ? params.get(k) : null);
  const sayfa = pozitifTam(al('sayfa'));
  return {
    ara: (al('ara') ?? '').trim(),
    salon: pozitifTam(al('salon')),
    degerlendirilemeyen: al('degerlendirilemeyen') === '1',
    sayfa: sayfa ? sayfa - 1 : 0,
  };
}

/** The state -> URLSearchParams, keeping every parameter of `mevcut` that is not ours; empty values and page 1 are left out so the address stays clean. */
export function adresYaz(durum, mevcut) {
  const out = new URLSearchParams(mevcut ? mevcut.toString() : '');
  ANAHTARLAR.forEach((k) => out.delete(k));
  const d = durum || {};
  if (d.ara && String(d.ara).trim() !== '') out.set('ara', String(d.ara).trim());
  if (pozitifTam(d.salon)) out.set('salon', String(pozitifTam(d.salon)));
  if (d.degerlendirilemeyen) out.set('degerlendirilemeyen', '1');
  if (Number.isInteger(d.sayfa) && d.sayfa > 0) out.set('sayfa', String(d.sayfa + 1));
  return out;
}

/** The state -> the query parameters of the endpoint (undefined = not sent). */
export function apiParametreleri(durum, boyut = SAYFA_BOYUTU) {
  const d = durum || {};
  return {
    search: d.ara ? String(d.ara).trim() || undefined : undefined,
    salonId: pozitifTam(d.salon) ?? undefined,
    degerlendirilemeyen: d.degerlendirilemeyen ? true : undefined,
    page: Number.isInteger(d.sayfa) && d.sayfa > 0 ? d.sayfa : 0,
    size: boyut,
  };
}

/**
 * The stacked bar of one row, as widths in percent of the bar: the free part and the excess part of the REQUEST, the whole bar scaled to the largest request on
 * the page (`enBuyukKw`). A row that cannot be judged is one grey part (`belirsiz`). Never throws; every width is 0..100.
 */
export function cubukOranlari(satir, enBuyukKw) {
  const istenen = Number(satir?.istenenKw);
  const enBuyuk = Number(enBuyukKw);
  if (!Number.isFinite(istenen) || istenen <= 0 || !Number.isFinite(enBuyuk) || enBuyuk <= 0) return { serbest: 0, asan: 0, belirsiz: 0 };
  const toplam = Math.min(100, (istenen / enBuyuk) * 100);
  if (satir.asanKw === null || satir.asanKw === undefined || satir.serbestKw === null || satir.serbestKw === undefined) {
    return { serbest: 0, asan: 0, belirsiz: toplam };
  }
  const asan = Math.min(istenen, Math.max(0, Number(satir.asanKw)));
  const asanPay = (asan / istenen) * toplam;
  return { serbest: toplam - asanPay, asan: asanPay, belirsiz: 0 };
}

/** The largest request kW among the rows of a page (for the bars), 0 when there is none. */
export function enBuyukIstenen(kayitlar) {
  return (kayitlar ?? []).reduce((m, s) => {
    const n = Number(s?.istenenKw);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
}

/**
 * Where a row opens: the stand's request page on the Elektrik card, with a way back to THIS page (the stand page reads ?geri= / ?geriEtiket=, like the person pages).
 * `geri` is the current path + search (an in-app address).
 */
export function talepSayfasiYolu(standId, geri, geriEtiket) {
  const params = new URLSearchParams({ tab: 'teknikTalepler', kategori: 'elektrik' });
  if (typeof geri === 'string' && geri.startsWith('/') && !geri.startsWith('//')) params.set('geri', geri);
  if (geriEtiket) params.set('geriEtiket', String(geriEtiket).slice(0, 60));
  return `/Firma/Talepler/${standId}?${params.toString()}`;
}
